<template>
  <section>
    <div class="guests-page__head" style="margin: 4px 0 12px">
      <h2 class="guests-page__title" style="font-size: 18px">
        Ospiti della struttura
      </h2>
      <button class="villa-btn villa-btn--primary" @click="openCreate">
        <i class="fa-solid fa-plus"></i> Nuovo ospite
      </button>
    </div>

    <div class="search-container" style="margin-bottom: 14px">
      <i v-if="!search" class="fa-solid fa-magnifying-glass search-icon"></i>
      <input
        v-model="search"
        type="text"
        class="search-box"
        placeholder="Cerca ospite…"
      />
      <button v-if="search" class="clear-search-button" @click="search = ''">
        <i class="fa-solid fa-times"></i>
      </button>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento…</div>
    <div v-else-if="filtered.length === 0" class="guests-loading">
      Nessun ospite{{
        search ? " per la ricerca" : " assegnato a questa struttura"
      }}.
    </div>

    <div v-else class="guests-table-wrap">
      <table class="guests-table">
        <thead>
          <tr>
            <th>Cognome</th>
            <th>Nome</th>
            <th>Retta mensile</th>
            <th>Stato</th>
            <th class="actions-cell">Azioni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="g in filtered" :key="g.id">
            <td>{{ g.lastName }}</td>
            <td>{{ g.firstName }}</td>
            <td>{{ feeLabel(g.monthlyFee) }}</td>
            <td>
              <span :class="['status-badge', 'status-badge--' + g.status]">
                {{ statusLabel(g.status) }}
              </span>
            </td>
            <td class="actions-cell">
              <router-link
                :to="`/guests/${g.id}`"
                class="icon-btn"
                title="Dettaglio"
              >
                <i class="fa-solid fa-eye"></i>
              </router-link>
              <button class="icon-btn" title="Modifica" @click="openEdit(g)">
                <i class="fa-solid fa-pen-to-square"></i>
              </button>
              <button
                class="icon-btn icon-btn--danger"
                title="Archivia"
                @click="askArchive(g)"
              >
                <i class="fa-solid fa-trash-can"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modale crea/modifica ospite (in contesto struttura) -->
    <VueFinalModal
      v-model="formModal.open"
      content-class="villa-modal villa-modal--wide"
    >
      <h3 class="villa-modal__title">
        {{ formModal.mode === "edit" ? "Modifica ospite" : "Nuovo ospite" }}
      </h3>
      <GuestForm
        v-if="formModal.open"
        :initial="formModal.initial"
        :submitting="formModal.busy"
        :locked-facility-id="facilityId"
        :locked-facility-name="facilityName"
        :submit-label="formModal.mode === 'edit' ? 'Salva' : 'Crea ospite'"
        @submit="onSubmit"
        @cancel="formModal.open = false"
      />
    </VueFinalModal>

    <!-- Conferma archiviazione -->
    <VueFinalModal v-model="confirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">Archivia ospite</h3>
      <p class="villa-confirm__text">
        Sei sicuro di voler archiviare
        <strong>{{ confirm.name }}</strong
        >?
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="confirm.busy"
          @click="confirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="confirm.busy"
          @click="confirmArchive"
        >
          {{ confirm.busy ? "Archivia…" : "Archivia" }}
        </button>
      </div>
    </VueFinalModal>
  </section>
</template>

<script>
import { mapActions } from "vuex";
import GuestForm from "@/components/GuestForm.vue";

const STATUS = {
  active: "Attivo",
  discharged: "Dimesso",
  suspended: "Sospeso",
  deceased: "Deceduto",
};

export default {
  name: "FacilityGuestsPanel",
  components: { GuestForm },
  props: {
    facilityId: { type: [String, Number], required: true },
    facilityName: { type: String, default: "" },
  },
  emits: ["changed"],
  data() {
    return {
      guests: [],
      loading: true,
      search: "",
      formModal: { open: false, mode: "create", busy: false, initial: null },
      confirm: { open: false, busy: false, id: null, name: "" },
    };
  },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.guests;
      return this.guests.filter((g) =>
        `${g.lastName} ${g.firstName}`.toLowerCase().includes(q)
      );
    },
  },
  methods: {
    ...mapActions([
      "fetchFacilityGuests",
      "fetchGuestById",
      "createGuest",
      "updateGuest",
      "archiveGuest",
    ]),
    statusLabel(s) {
      return STATUS[s] || s;
    },
    feeLabel(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} / mese`;
    },
    async load() {
      this.loading = true;
      try {
        this.guests = await this.fetchFacilityGuests(this.facilityId);
      } catch (err) {
        if (err?.response?.status !== 404) {
          this.$toast.error("Errore nel caricamento degli ospiti.");
        }
        this.guests = [];
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.formModal = {
        open: true,
        mode: "create",
        busy: false,
        initial: null,
      };
    },
    async openEdit(g) {
      try {
        const full = await this.fetchGuestById(g.id);
        this.formModal = {
          open: true,
          mode: "edit",
          busy: false,
          initial: full,
        };
      } catch {
        this.$toast.error("Impossibile caricare l'ospite.");
      }
    },
    async onSubmit(payload) {
      this.formModal.busy = true;
      try {
        if (this.formModal.mode === "edit") {
          await this.updateGuest({
            guestId: this.formModal.initial.id,
            payload,
          });
          this.$toast.success("Ospite aggiornato.");
        } else {
          await this.createGuest(payload);
          this.$toast.success("Ospite creato nella struttura.");
        }
        this.formModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.response?.data?.details ||
            "Salvataggio ospite non riuscito."
        );
      } finally {
        this.formModal.busy = false;
      }
    },
    askArchive(g) {
      this.confirm = {
        open: true,
        busy: false,
        id: g.id,
        name: `${g.firstName} ${g.lastName}`,
      };
    },
    async confirmArchive() {
      this.confirm.busy = true;
      try {
        await this.archiveGuest(this.confirm.id);
        this.$toast.success("Ospite archiviato.");
        this.confirm.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Impossibile archiviare l'ospite."
        );
      } finally {
        this.confirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
